import React, { useState, useEffect, useMemo } from 'react';
import TrainingProgress from './TrainingProgress';
import { API_BASE } from '../../lib/api';
import { mergeDeep, cloneDeep, formatMessage } from '../../utils/i18n';

const API = API_BASE;

const DEFAULT_COPY = {
  loading: 'Loading training progress data...',
  loadError: 'Failed to load training progress data',
  header: {
    detail: 'Training Progress - {name}',
    back: '← Back to All Students',
  },
  summary: {
    totalStudents: {
      heading: 'Total Students',
      description: '{count} students',
    },
    preTrip: {
      heading: 'PreTrip Completed',
      description: '{percent}%',
    },
    skills: {
      heading: 'Skills Completed',
      description: '{percent}%',
    },
    certified: {
      heading: 'Fully Certified',
      description: '{percent}%',
    },
  },
  table: {
    columns: {
      student: 'Student',
      progress: 'Progress',
      preTrip: 'PreTrip',
      skills: 'Skills',
      road: 'Road Test',
      actions: 'Actions',
    },
    status: {
      completed: 'Completed',
      inProgress: 'In Progress',
    },
    viewDetails: 'View Details',
    empty: 'No student progress available yet.',
  },
};

export default function AdminTrainingProgress({ copy: copyOverrides, trainingCopy }) {
  const copy = useMemo(() => mergeDeep(cloneDeep(DEFAULT_COPY), copyOverrides || {}), [copyOverrides]);
  const [summary, setSummary] = useState(null);
  const [allProgress, setAllProgress] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);

      const summaryResponse = await fetch(`${API}/api/training-progress/summary`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('go2cdl_access_token')}` },
        credentials: 'include',
      });

      const progressResponse = await fetch(`${API}/api/training-progress/all`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('go2cdl_access_token')}` },
        credentials: 'include',
      });

      if (summaryResponse.ok && progressResponse.ok) {
        const summaryData = await summaryResponse.json();
        const progressData = await progressResponse.json();

        if ((summaryData && summaryData.success === false) || (progressData && progressData.success === false)) {
          throw new Error('Unexpected response format');
        }

        const summaryRaw = summaryData.summary || {};
        const totalStudents = summaryRaw.totalStudents ?? summaryRaw.totalUsers ?? 0;
        const fullyCertified = summaryRaw.fullyCertified ?? summaryRaw.completedUsers ?? 0;

        setSummary({
          totalStudents,
          preTripCompleted: summaryRaw.preTripCompleted ?? 0,
          preTripPercentage: summaryRaw.preTripPercentage ?? 0,
          skillsCompleted: summaryRaw.skillsCompleted ?? 0,
          skillsPercentage: summaryRaw.skillsPercentage ?? 0,
          fullyCertified,
          fullyCertifiedPercentage: summaryRaw.fullyCertifiedPercentage ?? (
            totalStudents > 0 ? Math.round((fullyCertified / totalStudents) * 100) : 0
          ),
        });

        setAllProgress(progressData.students || []);
      } else {
        throw new Error('Failed to fetch training progress data');
      }
    } catch (err) {
      console.error('Error fetching training progress:', err);
      setError(copy.loadError);
    } finally {
      setLoading(false);
    }
  };

  const isPassedStatus = (status) => {
    if (!status) return false;
    const upper = String(status).toUpperCase();
    return upper === 'PASSED' || upper === 'DONE';
  };

  const calculateStudentProgress = (progress) => {
    const allFields = [
      'preTripInsideCab', 'preTripBrakes', 'preTripCoupling', 'preTripTractorFront', 'preTripTractorSide', 'preTripTrailer',
      'skill1', 'skill2', 'skill3', 'skill4',
      'roadTest'
    ];
    const passedCount = allFields.filter(field => isPassedStatus(progress[field])).length;
    return Math.round((passedCount / allFields.length) * 100);
  };

  const getStatusColor = (status) => {
    switch (String(status || '').toUpperCase()) {
      case 'PASSED':
      case 'DONE':
        return 'text-green-600';
      case 'NOT_STARTED':
        return 'text-red-600';
      case 'IN_PROGRESS':
        return 'text-yellow-600';
      case 'NEEDS_REVIEW':
        return 'text-orange-600';
      default:
        return 'text-gray-600';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-gray-600">{copy.loading}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-600 p-4 text-center">
        {error}
      </div>
    );
  }

  if (selectedStudent) {
    const name = `${selectedStudent.user.firstName || ''} ${selectedStudent.user.lastName || ''}`.trim();
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">
            {formatMessage(copy.header.detail, { name })}
          </h2>
          <button
            onClick={() => setSelectedStudent(null)}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
          >
            {copy.header.back}
          </button>
        </div>
        <TrainingProgress userId={selectedStudent.userId} isAdmin={true} copy={trainingCopy} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg p-6 shadow-sm border">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">{copy.summary.totalStudents.heading}</p>
                <p className="text-2xl font-semibold text-gray-900">{summary.totalStudents}</p>
                <p className="text-xs text-gray-500">{formatMessage(copy.summary.totalStudents.description, { count: summary.totalStudents })}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-6 shadow-sm border">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">{copy.summary.preTrip.heading}</p>
                <p className="text-2xl font-semibold text-gray-900">{summary.preTripCompleted}</p>
                <p className="text-xs text-gray-500">{formatMessage(copy.summary.preTrip.description, { percent: summary.preTripPercentage })}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-6 shadow-sm border">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">{copy.summary.skills.heading}</p>
                <p className="text-2xl font-semibold text-gray-900">{summary.skillsCompleted}</p>
                <p className="text-xs text-gray-500">{formatMessage(copy.summary.skills.description, { percent: summary.skillsPercentage })}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-6 shadow-sm border">
            <div className="flex items-center">
              <div className="p-2 bg-orange-100 rounded-lg">
                <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">{copy.summary.certified.heading}</p>
                <p className="text-2xl font-semibold text-gray-900">{summary.fullyCertified}</p>
                <p className="text-xs text-gray-500">{formatMessage(copy.summary.certified.description, { percent: summary.fullyCertifiedPercentage })}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {copy.table.columns.student}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {copy.table.columns.progress}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {copy.table.columns.preTrip}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {copy.table.columns.skills}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {copy.table.columns.road}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {copy.table.columns.actions}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {allProgress.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-sm text-center text-gray-500">
                    {copy.table.empty}
                  </td>
                </tr>
              )}
              {allProgress.map((progress) => (
                <tr key={progress.userId} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                          <span className="text-sm font-medium text-gray-700">
                            {progress.user.firstName?.[0]}{progress.user.lastName?.[0]}
                          </span>
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {progress.user.firstName} {progress.user.lastName}
                        </div>
                        <div className="text-sm text-gray-500">{progress.user.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-1">
                        <div className="w-24 bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full"
                            style={{ width: `${calculateStudentProgress(progress)}%` }}
                          ></div>
                        </div>
                      </div>
                      <div className="ml-2 text-sm text-gray-900">
                        {calculateStudentProgress(progress)}%
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`text-sm font-medium ${getStatusColor(
                      isPassedStatus(progress.preTripInsideCab) &&
                      isPassedStatus(progress.preTripBrakes) &&
                      isPassedStatus(progress.preTripCoupling) &&
                      isPassedStatus(progress.preTripTractorFront) &&
                      isPassedStatus(progress.preTripTractorSide) &&
                      isPassedStatus(progress.preTripTrailer)
                        ? 'PASSED'
                        : 'IN_PROGRESS'
                    )}`}>
                      {isPassedStatus(progress.preTripInsideCab) &&
                      isPassedStatus(progress.preTripBrakes) &&
                      isPassedStatus(progress.preTripCoupling) &&
                      isPassedStatus(progress.preTripTractorFront) &&
                      isPassedStatus(progress.preTripTractorSide) &&
                      isPassedStatus(progress.preTripTrailer)
                        ? copy.table.status.completed
                        : copy.table.status.inProgress}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`text-sm font-medium ${getStatusColor(
                      isPassedStatus(progress.skill1) &&
                      isPassedStatus(progress.skill2) &&
                      isPassedStatus(progress.skill3) &&
                      isPassedStatus(progress.skill4)
                        ? 'PASSED'
                        : 'IN_PROGRESS'
                    )}`}>
                      {isPassedStatus(progress.skill1) &&
                      isPassedStatus(progress.skill2) &&
                      isPassedStatus(progress.skill3) &&
                      isPassedStatus(progress.skill4)
                        ? copy.table.status.completed
                        : copy.table.status.inProgress}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`text-sm font-medium ${getStatusColor(progress.roadTest)}`}>
                      {isPassedStatus(progress.roadTest) ? copy.table.status.completed : copy.table.status.inProgress}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => setSelectedStudent(progress)}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      {copy.table.viewDetails}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
